import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, DestroyRef, effect, NgZone } from '@angular/core';
import { MessageService } from 'primeng/api';
import { TranslocoService } from '@jsverse/transloco';
import { Observable, Subscription, catchError, forkJoin, map, of } from 'rxjs';
import { environment } from '../../../environments/environment.development';
import { StreamInitDto } from '../api/models/inquiry.dto';
import { InquiriesApiService } from '../api/services/inquiries-api.service';
import { InquiryThreadsService, StoredThread } from './inquiry-threads.service';

const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_BASE_DELAY = 5000;
const RECONNECT_MAX_DELAY = 60000;

/**
 * `GuestTokenGuard` codes meaning „this guest token never opens the thread
 * again”: unknown / revoked (K15 — the guest deleted the request the thread
 * answers) or expired.
 */
const DEAD_GUEST_TOKEN_CODES: ReadonlySet<string> = new Set([
  'INQUIRY.INVALID_GUEST_TOKEN',
  'INQUIRY.GUEST_TOKEN_EXPIRED',
]);

/** Only the backend's definitive answer — never a network error, 429, 5xx or a bare 401 of a proxy. */
function isDeadGuestToken(err: unknown): boolean {
  if (!(err instanceof HttpErrorResponse) || err.status !== 401) return false;
  const body: unknown = err.error;
  const code = typeof body === 'object' && body !== null ? (body as { errorCode?: unknown }).errorCode : undefined;
  return typeof code === 'string' && DEAD_GUEST_TOKEN_CODES.has(code);
}

@Injectable({ providedIn: 'root' })
export class InquiryEventsService {
  private readonly threadsService = inject(InquiryThreadsService);
  private readonly api = inject(InquiriesApiService);
  private readonly messageService = inject(MessageService);
  private readonly transloco = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly zone = inject(NgZone);

  private eventSource: EventSource | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempt = 0;
  private connectedThreadIds: string[] = [];
  /** `stream.init` arrived on the current EventSource (the backend accepted the stream). */
  private streamInitialised = false;
  /** The tokens of the current thread set were already checked (once per `connect()`). */
  private tokensChecked = false;
  /** In-flight token checks — cancelled when the service is destroyed. */
  private readonly tokenChecks = new Subscription();

  constructor() {
    this.destroyRef.onDestroy(() => this.dispose());

    // Effect created in root injector context — survives component destruction.
    // Previously created inside start() which was called from HeaderComponent,
    // tying the effect to HeaderComponent's lifecycle. When user navigated to
    // pages without header (e.g. property-detail), the effect was destroyed
    // and new threads were never subscribed to SSE.
    effect(() => {
      const threads = this.threadsService.threads();
      const activeId = this.threadsService.activeThreadId();
      this.syncConnection(threads, activeId);
    });
  }

  /** @deprecated Effect is now auto-started in constructor. Kept for backwards compatibility. */
  start(): void {
    // no-op — effect is created in constructor
  }

  private syncConnection(threads: StoredThread[], activeId: string | null): void {
    const backgroundThreads = threads.filter(t => t.threadId !== activeId);

    if (backgroundThreads.length === 0) {
      this.disconnect();
      return;
    }

    const wantedIds = backgroundThreads.map(t => t.threadId).sort();
    const currentIds = [...this.connectedThreadIds].sort();

    if (
      this.eventSource &&
      wantedIds.length === currentIds.length &&
      wantedIds.every((id, i) => id === currentIds[i])
    ) {
      return;
    }

    this.disconnect();
    this.connect(backgroundThreads);
  }

  private connect(threads: StoredThread[]): void {
    if (threads.length === 0) return;

    this.reconnectAttempt = 0;
    this.tokensChecked = false;
    this.connectedThreadIds = threads.map(t => t.threadId);
    this.createEventSource(threads);
  }

  private createEventSource(threads: StoredThread[]): void {
    const threadsParam = threads
      .map(t => `${t.threadId}:${t.token}`)
      .join(',');

    const url = `${environment.apiUrl}/events/guest/stream?threads=${encodeURIComponent(threadsParam)}`;
    const es = new EventSource(url);
    this.streamInitialised = false;

    es.addEventListener('stream.init', ((e: MessageEvent) => {
      this.zone.run(() => {
        try {
          const init = JSON.parse(e.data) as StreamInitDto;
          this.streamInitialised = true;

          if (init.failedThreads?.length) {
            for (const failed of init.failedThreads) {
              this.threadsService.removeThread(failed.threadId);
            }
          }

          if (init.connectedThreads?.length) {
            this.threadsService.applyStreamSummaries(init.connectedThreads);
          }

          this.reconnectAttempt = 0;
        } catch { /* ignore parse errors */ }
      });
    }) as EventListener);

    es.addEventListener('inquiry.new_message', ((e: MessageEvent) => {
      this.zone.run(() => {
        try {
          const { threadId, data } = JSON.parse(e.data) as {
            threadId: string;
            data: {
              content?: string;
              messagePreview?: string;
              message?: string;
              senderType: 'guest' | 'host';
            };
          };

          const content = data.content || data.messagePreview || data.message || '';
          this.threadsService.updateLastMessage(threadId, content, data.senderType);

          if (data.senderType === 'host') {
            this.threadsService.incrementUnread(threadId);

            const thread = this.threadsService.getThread(threadId);
            const sender = thread?.propertyName || this.transloco.translate('chat.host');
            this.messageService.add({
              key: 'chat',
              severity: 'info',
              summary: this.transloco.translate('chat.newMessageFrom'),
              detail: content.length > 80 ? content.substring(0, 80) + '…' : content,
              life: 8000,
              // No guest token: „Odpowiedz” opens the chat by threadId only
              // (the chat page reads the token from InquiryThreadsService).
              data: { threadId, sender },
            });
          }
        } catch { /* ignore parse errors */ }
      });
    }) as EventListener);

    es.addEventListener('inquiry.closed', ((e: MessageEvent) => {
      this.zone.run(() => {
        try {
          const { threadId } = JSON.parse(e.data) as { threadId: string };
          this.connectedThreadIds = this.connectedThreadIds.filter(id => id !== threadId);
        } catch { /* ignore parse errors */ }
      });
    }) as EventListener);

    es.onerror = () => {
      this.zone.run(() => {
        const refusedBeforeInit = !this.streamInitialised;
        this.closeEventSource();
        if (refusedBeforeInit) {
          this.checkTokensOnce(threads);
        }
        this.scheduleReconnect(threads);
      });
    };

    this.eventSource = es;
  }

  /**
   * The stream failed before `stream.init`. When EVERY token is dead the backend
   * refuses the whole stream (400 „No valid thread tokens provided”), so the
   * dead threads are never reported in `failedThreads` and would stay in the
   * list (header icon, `/inquiry/reply`) for good — while EventSource cannot
   * tell that 400 from a network error or a 5xx of a deploy.
   *
   * So each token of the failed set is checked ONCE per thread set (GET
   * host-status: the same token guard as the stream — no message is read or
   * marked as read). A thread leaves the list only on the guard's
   * definitive answer (401 INVALID_GUEST_TOKEN / GUEST_TOKEN_EXPIRED) and only
   * if it still holds the checked token (a newer link may have replaced it);
   * anything else keeps it. The dead ones leave in one update → the thread set
   * changes once → the stream reconnects with the rest, or stays closed when
   * nothing is left. Otherwise the bounded reconnect goes on as before.
   */
  private checkTokensOnce(threads: StoredThread[]): void {
    if (this.tokensChecked || threads.length === 0) return;
    this.tokensChecked = true;

    const checks: Observable<StoredThread | null>[] = threads.map((thread) =>
      this.api.getHostStatus(thread.threadId, thread.token).pipe(
        map((): StoredThread | null => null),
        catchError((err: unknown) => of(isDeadGuestToken(err) ? thread : null)),
      ),
    );
    this.tokenChecks.add(
      forkJoin(checks).subscribe((results) => {
        this.zone.run(() => {
          for (const dead of results) {
            if (dead && this.threadsService.getThread(dead.threadId)?.token === dead.token) {
              this.threadsService.removeThread(dead.threadId);
            }
          }
        });
      }),
    );
  }

  private scheduleReconnect(threads: StoredThread[]): void {
    if (this.reconnectAttempt >= MAX_RECONNECT_ATTEMPTS) return;

    const delay = Math.min(
      RECONNECT_BASE_DELAY * Math.pow(2, this.reconnectAttempt),
      RECONNECT_MAX_DELAY,
    );
    this.reconnectAttempt++;

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.createEventSource(threads);
    }, delay);
  }

  private disconnect(): void {
    this.cancelReconnect();
    this.closeEventSource();
    this.connectedThreadIds = [];
  }

  private closeEventSource(): void {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }

  private cancelReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private dispose(): void {
    this.disconnect();
    this.tokenChecks.unsubscribe();
  }
}
